import { Edge, Node } from 'reactflow';

const TRIGGER_ACTIONS = new Set([
   'start', 'webhook',
   'whatsappListener', 'whatsappCloudTrigger', 'metaListenerTrigger',
   'shopifyTrigger', 'odooTrigger', 'easyOrdersTrigger', 'bostaTrigger', 'woocommerceTrigger',
   'sqlTrigger', 'googleDriveTrigger', 'googleCalendarTrigger', 'slackTrigger',
   'typeformTrigger', 'googleFormTrigger', 'outlookTrigger', 'imapTrigger',
]);

/** Detect failed node outputs from the run stream (avoid false positives). */
export function isExecutionErrorOutput(output: any): boolean {
   if (output == null || typeof output !== 'object' || Array.isArray(output)) return false;

   const status = output.status ?? output.Output?.status;
   if (status === 'error' || status === 'failed') return true;

   const err = output.error ?? output.Output?.error;
   if (err === true) return true;
   if (typeof err === 'string' && err.trim().length > 0) return true;
   if (typeof err === 'object' && err !== null) {
      const msg = err.message ?? err.msg ?? err.code;
      if (typeof msg === 'string' && msg.trim().length > 0) return true;
   }
   return false;
}

/** Clear execution highlight flags (and optionally outputs) at run start / clear. */
export function clearExecutionProgress<T extends Node<any>>(
   nodes: T[],
   options?: { clearOutput?: boolean }
): T[] {
   const clearOutput = options?.clearOutput !== false;
   return nodes.map((node) => {
      if (node.data?.action === 'output') {
         return {
            ...node,
            data: {
               ...node.data,
               ...(clearOutput ? { value: '' } : {}),
               isExecuting: false,
               executionError: false,
            },
         };
      }
      if (node.data?.isMediator) {
         return {
            ...node,
            data: { ...node.data, isExecuting: false, executionError: false },
         };
      }
      return {
         ...node,
         data: {
            ...node.data,
            ...(clearOutput ? { output: '' } : {}),
            isExecuting: false,
            executionError: false,
         },
      };
   });
}

/** Mark graph entry nodes as executing when a run begins. */
export function markRunEntryNodesExecuting<T extends Node<any>>(
   nodes: T[],
   edges: Edge[],
   targetIndex?: string
): T[] {
   if (targetIndex) {
      return nodes.map((node) => ({
         ...node,
         data: {
            ...node.data,
            isExecuting: node.data?.index === targetIndex,
            executionError: false,
         },
      }));
   }

   const targetIds = new Set(edges.map((e) => e.target));
   const entryIds = new Set(
      nodes
         .filter((n) => {
            if (n.data?.action === 'output') return false;
            return TRIGGER_ACTIONS.has(String(n.data?.action || '')) || !targetIds.has(n.id);
         })
         .map((n) => n.id)
   );

   return nodes.map((node) => ({
      ...node,
      data: {
         ...node.data,
         isExecuting: entryIds.has(node.id),
         executionError: false,
      },
   }));
}

/**
 * After a node finishes, mark its downstream targets as the active hop(s).
 * On failure, stop advancing and flag the failed node.
 */
export function advanceExecutionProgress<T extends Node<any>>(
   nodes: T[],
   edges: Edge[],
   completedNodeId: string,
   completedIndex: string,
   completedOutput: any
): T[] {
   const failed = isExecutionErrorOutput(completedOutput);
   const nextTargetIds = failed
      ? new Set<string>()
      : new Set(edges.filter((e) => e.source === completedNodeId).map((e) => e.target));

   return nodes.map((node) => {
      if (node.data?.index === completedIndex || node.id === completedNodeId) {
         return {
            ...node,
            data: {
               ...node.data,
               output: completedOutput,
               isExecuting: false,
               executionError: failed,
            },
         };
      }

      if (nextTargetIds.has(node.id)) {
         return {
            ...node,
            data: {
               ...node.data,
               isExecuting: true,
               executionError: false,
            },
         };
      }

      return node;
   });
}
